import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Card,
  Stack,
  Table,
  Avatar,
  Button,
  Tooltip,
  Divider,
  Checkbox,
  MenuItem,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';

// hooks
import useAuth from '../../../hooks/useAuth';
import useTabs from '../../../hooks/useTabs';
import useSettings from '../../../hooks/useSettings';
import useTable, { getComparator, emptyRows } from '../../../hooks/useTable';
import useColumnVisibility, { ColumnDef } from 'src/hooks/useColumnVisibility';
// @types
import { User } from '../../../@types/user';
// _mock_
// components
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  TableNoData,
  TableMoreMenu,
  ColumnsButton,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedActions,
} from '../../../components/table';
// sections
import { UserTableToolbar } from '../../@dashboard/user/list';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { PackageModal } from './PackageModal';
import { Package } from 'src/@types/package';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

const Sort_Options = ['all', 'acsending', 'descending'];

const PACKAGES_COLUMNS: ColumnDef[] = [
  { id: 'Name', label: 'Name', align: 'left' },
  { id: 'Fees', label: 'Fees', align: 'left' },
  { id: 'Duration', label: 'Duration', align: 'left' },
];

// ----------------------------------------------------------------------

export default function PackageList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { enqueueSnackbar } = useSnackbar();

  const { visibleColumns, toggleColumn, isVisible, headLabel } = useColumnVisibility(
    PACKAGES_COLUMNS,
    'kayfa.packages.visibleColumns'
  );

  const { themeStretch } = useSettings();
  const { user } = useAuth();
  // Package write permissions (the BE enforces the same):
  //  - canManage (create + edit): admin / super-admin / customer-care / sales-manager
  //  - canDelete: admin / super-admin only (DELETE is admin-only on the BE)
  const roles: string[] = ((user as any)?.roles as string[]) ?? [];
  const canManage =
    roles.includes('admin') ||
    roles.includes('super-admin') ||
    roles.includes('customer-care') ||
    roles.includes('sales-manager');
  const canDelete = roles.includes('admin') || roles.includes('super-admin');

  const navigate = useNavigate();

  const [tableData, setTableData] = useState<Package[]>([]);

  const [filterRole, setFilterRole] = useState('all');
  const [filterName, setFilterName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPackage, setCurrentPackage] = useState<Package | undefined>();

  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs('all');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState({ open: false, currentId: '' });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    getPackageList().catch(console.error);
  }, []);
  const getPackageList = async () => {
    const data = await axiosInstance.get(APIS.PACKAGES.LIST);
    setTableData(data.data.data);
  };

  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setPage(0);
  };

  const handleFilterRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterRole(event.target.value);
  };

  const handleDeleteRows = (selected: string[]) => {
    const deleteRows = tableData?.filter((row) => !selected.includes(row._id as string));
    setSelected([]);
    setTableData(deleteRows);
  };
  const handleDeleteRow = async (id: string) => {
    try {
      setLoading(true);
      await axiosInstance.delete(APIS.PACKAGES.PATCH(id));
      const deleteRow = tableData?.filter((row) => row._id !== id);
      setSelected([]);
      setLoading(false);
      setTableData(deleteRow);
    } catch (error) {
      setLoading(false);
      console.error(error);
      enqueueSnackbar(error?.message || 'Unable to Delete!', { variant: 'error' });
    }
  };

  const handleEditRow = (selectedPackage: Package) => {
    setCurrentPackage(selectedPackage);
    setModalOpen(true);
  };

  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterRole,
    filterStatus,
  });

  const isNotFound =
    (!dataFiltered.length && !!filterName) ||
    (!dataFiltered.length && !!filterRole) ||
    (!dataFiltered.length && !!filterStatus);

  return (
    <Page title="Package: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Package List"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Package List', href: PATH_DASHBOARD.packages },
          ]}
          action={
            canManage ? (
              <Button
                variant="contained"
                onClick={() => setModalOpen(true)}
                startIcon={<Iconify icon={'eva:plus-fill'} />}
              >
                New Package
              </Button>
            ) : undefined
          }
        />

        <Card>
          <Divider />
          <UserTableToolbar
            placeholder="Search Package..."
            filterName={filterName}
            filterRole={filterRole}
            onFilterName={handleFilterName}
            onFilterRole={handleFilterRole}
            optionsRole={Sort_Options}
          />

          <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, pb: 1 }}>
            <ColumnsButton
              columns={PACKAGES_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              {selected.length > 0 && canDelete && (
                <TableSelectedActions
                  dense={dense}
                  numSelected={selected.length}
                  rowCount={tableData?.length}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData?.map((row) => row._id as string)
                    )
                  }
                  actions={
                    <Tooltip title="Delete">
                      <IconButton color="primary" onClick={() => handleDeleteRows(selected)}>
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              )}

              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={[...headLabel, { id: '' }, { id: '' }, { id: '' }]}
                  rowCount={tableData?.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData?.map((row) => row._id as string)
                    )
                  }
                />

                <TableBody>
                  {dataFiltered.length > 0 &&
                    dataFiltered
                      ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      ?.map((row) => (
                        <PackageRow
                          key={row._id}
                          row={row}
                          isVisible={isVisible}
                          canManage={canManage}
                          canDelete={canDelete}
                          selected={selected.includes(row._id as string)}
                          onSelectRow={() => onSelectRow(row._id as string)}
                          onDeleteRow={() =>
                            setConfirmOpen({ open: true, currentId: row._id as string })
                          }
                          onEditRow={() => handleEditRow(row)}
                        />
                      ))}

                  <TableEmptyRows
                    height={2.2}
                    emptyRows={emptyRows(page, rowsPerPage, tableData?.length)}
                  />

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>
      </Container>
      <PackageModal
        isEdit={currentPackage ? true : false}
        currentPackage={currentPackage}
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          getPackageList();
          setCurrentPackage(undefined);
        }}
      />
      <ConfirmationDialog
        isOpen={confirmOpen.open}
        onClose={() => {
          setConfirmOpen({ open: false, currentId: '' });
          getPackageList();
        }}
        confirmAction={() => handleDeleteRow(confirmOpen.currentId)}
        loading={loading}
        text={'Are You Sure You want To Delete This Package'}
        headText={'Delete Package'}
      />
    </Page>
  );
}

// ----------------------------------------------------------------------

type PackageRowProps = {
  row: Package;
  selected: boolean;
  isVisible: (id: string) => boolean;
  // canManage → Edit; canDelete → Delete. A row shows the actions menu only
  // when at least one of them is allowed.
  canManage?: boolean;
  canDelete?: boolean;
  onEditRow: VoidFunction;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
};

function PackageRow({
  row,
  selected,
  isVisible,
  canManage = true,
  canDelete = true,
  onEditRow,
  onSelectRow,
  onDeleteRow,
}: PackageRowProps) {
  const { name, cost, icon, duration } = row;

  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </TableCell>

      {isVisible('Name') && (
        <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar alt={name} src={icon} sx={{ mr: 2 }} />
          <Typography variant="subtitle2" noWrap>
            {name}
          </Typography>
        </TableCell>
      )}

      {isVisible('Fees') && <TableCell align="left">{cost}</TableCell>}
      {isVisible('Duration') && <TableCell align="left">{duration}</TableCell>}

      <TableCell align="left" />
      <TableCell align="left" />

      <TableCell align="right">
        {(canManage || canDelete) && (
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            actions={
              <>
                {canManage && (
                  <MenuItem
                    onClick={() => {
                      onEditRow();
                      handleCloseMenu();
                    }}
                  >
                    <Iconify icon={'eva:edit-fill'} />
                    Edit
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem
                    onClick={() => {
                      onDeleteRow();
                      handleCloseMenu();
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <Iconify icon={'eva:trash-2-outline'} />
                    Delete
                  </MenuItem>
                )}
              </>
            }
          />
        )}
      </TableCell>
    </TableRow>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({
  tableData,
  comparator,
  filterName,
  filterStatus,
  filterRole,
}: {
  tableData: Package[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterStatus: string;
  filterRole: string;
}) {
  const stabilizedThis = tableData?.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData?.filter(
      (item: Record<string, any>) =>
        item.name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData?.filter((item: Record<string, any>) => item.status === filterStatus);
  }

  if (filterRole !== 'all') {
    tableData = tableData?.filter((item: Record<string, any>) => item.role === filterRole);
  }

  return tableData;
}
